import React, { useState, useEffect } from 'react';
import { packageService } from '../../services/packageService';
import { Package, CreatePackageRequest, currencyOptions } from '../../types/package';
import { useToast } from '../../contexts/ToastContext';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editPackage: Package | null;
}

const CreateEditPackageModal: React.FC<Props> = ({ isOpen, onClose, onSuccess, editPackage }) => {
  const { toast } = useToast();
  const isEdit = !!editPackage;

  const [form, setForm] = useState({
    name: '',
    description: '',
    monthlyPrice: '',
    currency: 'USD',
    maxUsers: '',
    maxWordPressSites: '',
    monthlyContentLimit: '',
    overageContentPrice: '',
    sortOrder: '',
    bulkContentEnabled: false,
    maxBulkContentPerRequest: '1',
    linkAssistantEnabled: false,
    productionPriority: '0',
    visualQuality: 'Basic' as 'Basic' | 'Advanced' | 'Premium',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      if (editPackage) {
        const vq = editPackage.visualQuality;
        const vqStr = typeof vq === 'number'
          ? (vq === 0 ? 'Basic' : vq === 1 ? 'Advanced' : 'Premium')
          : (vq || 'Basic');
        setForm({
          name: editPackage.name,
          description: editPackage.description || '',
          monthlyPrice: editPackage.monthlyPrice.toString(),
          currency: editPackage.currency,
          maxUsers: editPackage.maxUsers.toString(),
          maxWordPressSites: editPackage.maxWordPressSites.toString(),
          monthlyContentLimit: editPackage.monthlyContentLimit.toString(),
          overageContentPrice: editPackage.overageContentPrice.toString(),
          sortOrder: editPackage.sortOrder.toString(),
          bulkContentEnabled: editPackage.bulkContentEnabled ?? false,
          maxBulkContentPerRequest: (editPackage.maxBulkContentPerRequest ?? 1).toString(),
          linkAssistantEnabled: editPackage.linkAssistantEnabled ?? false,
          productionPriority: (editPackage.productionPriority ?? 0).toString(),
          visualQuality: vqStr as 'Basic' | 'Advanced' | 'Premium',
        });
      } else {
        setForm({
          name: '',
          description: '',
          monthlyPrice: '',
          currency: 'USD',
          maxUsers: '',
          maxWordPressSites: '',
          monthlyContentLimit: '',
          overageContentPrice: '0',
          sortOrder: '1',
          bulkContentEnabled: false,
          maxBulkContentPerRequest: '1',
          linkAssistantEnabled: false,
          productionPriority: '0',
          visualQuality: 'Basic',
        });
      }
      setErrors({});
    }
  }, [isOpen, editPackage]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) newErrors.name = 'Package name is required';
    if (!form.monthlyPrice || Number(form.monthlyPrice) < 0) newErrors.monthlyPrice = 'Valid price is required';
    if (!form.maxUsers || Number(form.maxUsers) < 1) newErrors.maxUsers = 'At least 1 user required';
    if (!form.maxWordPressSites || Number(form.maxWordPressSites) < 1) newErrors.maxWordPressSites = 'At least 1 site required';
    if (!form.monthlyContentLimit || Number(form.monthlyContentLimit) < 1) newErrors.monthlyContentLimit = 'Content limit is required';
    if (form.overageContentPrice && Number(form.overageContentPrice) < 0) newErrors.overageContentPrice = 'Cannot be negative';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const payload: CreatePackageRequest = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        monthlyPrice: Number(form.monthlyPrice),
        currency: form.currency,
        maxUsers: Number(form.maxUsers),
        maxWordPressSites: Number(form.maxWordPressSites),
        monthlyContentLimit: Number(form.monthlyContentLimit),
        overageContentPrice: Number(form.overageContentPrice) || 0,
        sortOrder: Number(form.sortOrder) || 1,
        bulkContentEnabled: form.bulkContentEnabled,
        maxBulkContentPerRequest: form.bulkContentEnabled ? Math.max(1, Number(form.maxBulkContentPerRequest) || 1) : 1,
        linkAssistantEnabled: form.linkAssistantEnabled,
        productionPriority: Math.max(0, Math.min(10, Number(form.productionPriority) || 0)),
        visualQuality: form.visualQuality,
      };

      let response;
      if (isEdit) {
        response = await packageService.update(editPackage!.id, payload);
      } else {
        response = await packageService.create(payload);
      }

      if (response.success) {
        toast.success(`${form.name} ${isEdit ? 'updated' : 'created'} successfully`);
        onSuccess();
        onClose();
      } else {
        toast.error(response.message || 'Operation failed');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateField = (field: string, value: string | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white border border-slate-200 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="size-10 bg-primary/20 rounded-xl flex items-center justify-center">
              <span className="material-symbols-outlined text-primary">
                {isEdit ? 'edit' : 'add_box'}
              </span>
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">{isEdit ? 'Edit Package' : 'Create New Package'}</h2>
              <p className="text-xs text-slate-500">
                {isEdit ? `Editing ${editPackage!.name}` : 'Define a new subscription tier'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="size-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors"
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Name & Currency Row */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
                Package Name *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={e => updateField('name', e.target.value)}
                placeholder="e.g. Professional"
                className={`w-full bg-slate-50 border rounded-lg px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-primary transition-colors ${
                  errors.name ? 'border-red-500' : 'border-slate-200'
                }`}
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
                Currency
              </label>
              <select
                value={form.currency}
                onChange={e => updateField('currency', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-primary transition-colors"
              >
                {currencyOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={e => updateField('description', e.target.value)}
              placeholder="Brief description of what this package offers..."
              rows={2}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-primary transition-colors resize-none"
            />
          </div>

          {/* Pricing Section */}
          <div>
            <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2">
              <span className="material-symbols-outlined text-sm">payments</span>
              Pricing
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-2">Monthly Price *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.monthlyPrice}
                  onChange={e => updateField('monthlyPrice', e.target.value)}
                  placeholder="29.00"
                  className={`w-full bg-slate-50 border rounded-lg px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-primary transition-colors ${
                    errors.monthlyPrice ? 'border-red-500' : 'border-slate-200'
                  }`}
                />
                {errors.monthlyPrice && <p className="text-red-500 text-xs mt-1">{errors.monthlyPrice}</p>}
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-2">Overage Price per Content</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.overageContentPrice}
                  onChange={e => updateField('overageContentPrice', e.target.value)}
                  placeholder="0.50"
                  className={`w-full bg-slate-50 border rounded-lg px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-primary transition-colors ${
                    errors.overageContentPrice ? 'border-red-500' : 'border-slate-200'
                  }`}
                />
                {errors.overageContentPrice && <p className="text-red-500 text-xs mt-1">{errors.overageContentPrice}</p>}
              </div>
            </div>
          </div>

          {/* Resource Limits */}
          <div>
            <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2">
              <span className="material-symbols-outlined text-sm">tune</span>
              Resource Limits
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-2">Max Users *</label>
                <input
                  type="number"
                  min="1"
                  value={form.maxUsers}
                  onChange={e => updateField('maxUsers', e.target.value)}
                  placeholder="10"
                  className={`w-full bg-slate-50 border rounded-lg px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-primary transition-colors ${
                    errors.maxUsers ? 'border-red-500' : 'border-slate-200'
                  }`}
                />
                {errors.maxUsers && <p className="text-red-500 text-xs mt-1">{errors.maxUsers}</p>}
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-2">WP Sites *</label>
                <input
                  type="number"
                  min="1"
                  value={form.maxWordPressSites}
                  onChange={e => updateField('maxWordPressSites', e.target.value)}
                  placeholder="5"
                  className={`w-full bg-slate-50 border rounded-lg px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-primary transition-colors ${
                    errors.maxWordPressSites ? 'border-red-500' : 'border-slate-200'
                  }`}
                />
                {errors.maxWordPressSites && <p className="text-red-500 text-xs mt-1">{errors.maxWordPressSites}</p>}
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-2">Content / mo *</label>
                <input
                  type="number"
                  min="1"
                  value={form.monthlyContentLimit}
                  onChange={e => updateField('monthlyContentLimit', e.target.value)}
                  placeholder="200"
                  className={`w-full bg-slate-50 border rounded-lg px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-primary transition-colors ${
                    errors.monthlyContentLimit ? 'border-red-500' : 'border-slate-200'
                  }`}
                />
                {errors.monthlyContentLimit && <p className="text-red-500 text-xs mt-1">{errors.monthlyContentLimit}</p>}
              </div>
            </div>
          </div>

          {/* Dynamic Features */}
          <div>
            <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2">
              <span className="material-symbols-outlined text-sm">bolt</span>
              Dinamik Özellikler
            </h3>

            <div className="space-y-3">
              {/* Bulk Content */}
              <div className="flex items-start gap-3 p-4 rounded-lg bg-slate-50 border border-slate-200">
                <label className="flex items-start gap-3 flex-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.bulkContentEnabled}
                    onChange={e => updateField('bulkContentEnabled', e.target.checked)}
                    className="mt-1 size-4 accent-primary"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-bold text-slate-900">Toplu İçerik Üretimi</p>
                    <p className="text-xs text-slate-500 mt-0.5">Kullanıcı tek istekte birden fazla içerik üretebilir.</p>
                  </div>
                </label>
                {form.bulkContentEnabled && (
                  <div className="w-32">
                    <label className="block text-[10px] font-bold text-slate-500 mb-1">Max Adet</label>
                    <input
                      type="number"
                      min="1"
                      max="50"
                      value={form.maxBulkContentPerRequest}
                      onChange={e => updateField('maxBulkContentPerRequest', e.target.value)}
                      placeholder="10"
                      className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-primary"
                    />
                  </div>
                )}
              </div>

              {/* Link Assistant */}
              <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.linkAssistantEnabled}
                    onChange={e => updateField('linkAssistantEnabled', e.target.checked)}
                    className="mt-1 size-4 accent-primary"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-bold text-slate-900">Link Asistanı (İç &amp; Dış Linkleme)</p>
                    <p className="text-xs text-slate-500 mt-0.5">Kapalıysa içerik oluşturma ekranında iç &amp; dış linkleme seçenekleri pasif görünür.</p>
                  </div>
                </label>
              </div>

              {/* Production Priority */}
              <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-bold text-slate-900">Üretim Önceliği</p>
                    <p className="text-xs text-slate-500">0 = düşük, 10 = en yüksek.</p>
                  </div>
                  <span className="inline-flex items-center justify-center size-10 rounded-lg bg-primary/10 text-primary font-black">
                    {form.productionPriority}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="10"
                  step="1"
                  value={form.productionPriority}
                  onChange={e => updateField('productionPriority', e.target.value)}
                  className="w-full accent-primary"
                />
                <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                  <span>0</span>
                  <span>5</span>
                  <span>10</span>
                </div>
              </div>

              {/* Visual Quality */}
              <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <p className="text-sm font-bold text-slate-900">Görsel Kalitesi</p>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {(['Basic', 'Advanced', 'Premium'] as const).map(q => (
                    <button
                      type="button"
                      key={q}
                      onClick={() => updateField('visualQuality', q)}
                      className={`py-2 rounded-lg text-xs font-bold border transition-all ${
                        form.visualQuality === q
                          ? 'bg-primary/10 border-primary text-primary'
                          : 'bg-white border-slate-200 text-slate-500'
                      }`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sort Order */}
          <div className="w-32">
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
              Sort Order
            </label>
            <input
              type="number"
              min="1"
              value={form.sortOrder}
              onChange={e => updateField('sortOrder', e.target.value)}
              placeholder="1"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-primary transition-colors"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-lg text-sm font-bold bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-2.5 rounded-lg text-sm font-bold bg-primary hover:bg-primary/90 text-white transition-all active:scale-95 shadow-lg shadow-primary/20 flex items-center gap-2"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {isEdit ? 'Saving...' : 'Creating...'}
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-base">
                  {isEdit ? 'save' : 'add'}
                </span>
                {isEdit ? 'Save Changes' : 'Create Package'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateEditPackageModal;
